<template>
  <div>
    <div :class="containerClass">
      <!-- Page head: title, one line, the date filter on the right -->
      <div class="ed-head">
        <div>
          <h2 class="ed-title">Home</h2>
          <p class="ed-lead">Welcome back, {{ firstName }}.</p>
        </div>
        <div class="ed-filter">
          <date-picker
            v-model="filters.dateRange"
            format="MMM D, YYYY"
            valueType="YYYY-MM-DD"
            id="date"
            lang="en"
            placeholder="All time"
            range
            class="ed-filter__picker"
            @input="updateFilters"
          ></date-picker>
        </div>
      </div>
    </div>
    <div class="page-section border-bottom-2">
      <div :class="containerClass">
        <div v-if="pendingNotifications.length" class="mb-4">
          <b-alert v-for="notification in pendingNotifications" :key="notification.id" show>
            <div class="d-flex">
              <router-link
                :to="{
                  name: 'feature-update-details',
                  params: { id: notification.extra.app_update_id },
                  query: { notification: notification.id },
                }"
              >
                {{ notification.extra.notification_title }}
              </router-link>
              <div class="ml-auto clickable-item" @click.prevent="setNotificationRead(notification.id)">
                <md-icon>close</md-icon>
              </div>
            </div>
          </b-alert>
          <div class="text-center" v-if="moreNotifications > 0">
            <router-link :to="{ name: 'feature-updates-page' }"> View {{ moreNotifications }} More </router-link>
          </div>
        </div>

        <!-- The six counts. Each tile opens its list. -->
        <div class="ed-stats">
          <router-link v-for="tile in tiles" :key="tile.key" :to="{ name: tile.route }" class="ed-stat">
            <span class="ed-stat__icon"><md-icon>{{ tile.icon }}</md-icon></span>
            <b-skeleton-wrapper :loading="isLoading[tile.key]">
              <template #loading>
                <span class="ed-stat__num"><b-skeleton width="60%" height="2rem"></b-skeleton></span>
              </template>
              <span class="ed-stat__num">{{ $n(tile.value) }}</span>
            </b-skeleton-wrapper>
            <span class="ed-stat__label">{{ tile.label }}</span>
            <span class="ed-stat__go"><md-icon>chevron_right</md-icon></span>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.ed-head { display: flex; align-items: flex-end; justify-content: space-between; gap: 16px 24px; flex-wrap: wrap; padding: 28px 0 20px; }
.ed-title { margin: 0; font-size: 32px; line-height: 1.1; }
.ed-lead { margin: 6px 0 0; font-size: 15px; color: rgba(24, 24, 24, 0.62); }
.ed-filter { flex: 0 1 320px; min-width: 240px; }
.ed-filter__picker { width: 100%; }

.ed-stats { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; }
.ed-stat {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 20px 22px 18px;
  background: #fff;
  border: 1px solid rgba(24, 24, 24, 0.1);
  border-radius: 14px;
  color: #181818 !important;
  text-decoration: none !important;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}
/* School brand colors come from App.vue as --primary-color / --dark-color. */
.ed-stat:hover { border-color: var(--primary-color, #3b86f5); box-shadow: 0 8px 20px rgba(24, 24, 24, 0.06); transform: translateY(-1px); }
.ed-stat__icon { display: inline-flex; align-items: center; justify-content: center; width: 36px; height: 36px; margin-bottom: 12px; border-radius: 10px; background: var(--primary-color, #3b86f5); color: #fff; .md-icon { font-size: 20px; } }
.ed-stat__num { font-family: 'Walter Neue', 'Diatype', sans-serif; font-weight: 800; font-size: 34px; line-height: 1.05; letter-spacing: -0.01em; color: #181818; }
.ed-stat__label { margin-top: 6px; font-size: 13px; font-weight: 500; color: rgba(24, 24, 24, 0.62); }
.ed-stat__go { position: absolute; top: 18px; right: 16px; color: rgba(24, 24, 24, 0.35); transition: color 0.15s ease, transform 0.15s ease; .md-icon { font-size: 20px; } }
.ed-stat:hover .ed-stat__go { color: var(--primary-color, #3b86f5); transform: translateX(2px); }

@media (max-width: 991px) { .ed-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 575px) {
  .ed-stats { grid-template-columns: 1fr; }
  .ed-head { padding-top: 20px; }
  .ed-filter { flex-basis: 100%; }
}
</style>
<script>
import { mapActions, mapGetters } from 'vuex';
import { get } from 'lodash';
import DatePicker from 'vue2-datepicker';
import moment from 'moment';
import Page from '@/components/Page.vue';
import { formatToAPIDate } from '../../../common/utils';

export default {
  components: { DatePicker },
  extends: Page,

  data() {
    return {
      title: 'Home Dashboard',

      totalRegistrations: 0,
      totalStudents: 0,
      totalPrograms: 0,
      totalClasses: 0,
      totalInstructors: 0,
      totalProducts: 0,

      filters: {
        dateRange: [],
      },

      isLoading: {
        totalRegistrations: false,
        totalStudents: false,
        totalPrograms: false,
        totalClasses: false,
        totalInstructors: false,
        totalProducts: false,
      },
      areNotificationsLoading: false,
      allNotifications: [],
      pendingNotifications: [],
      moreNotifications: 0,
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    firstName() {
      return get(this.getLoggedInUser, 'first_name') || 'there';
    },
    /** One row per count, in the order a school reads them: what they offer, who teaches it, who signed up. */
    tiles() {
      return [
        { key: 'totalPrograms', label: 'Programs', value: this.totalPrograms, icon: 'menu_book', route: 'manage-programs' },
        { key: 'totalClasses', label: 'Classes', value: this.totalClasses, icon: 'event', route: 'classes-management-list' },
        { key: 'totalInstructors', label: 'Instructors', value: this.totalInstructors, icon: 'school', route: 'instructor-management-list' },
        { key: 'totalRegistrations', label: 'Registrations', value: this.totalRegistrations, icon: 'how_to_reg', route: 'school-enrollment-report' },
        { key: 'totalStudents', label: 'Students', value: this.totalStudents, icon: 'people', route: 'schl-students-list' },
        { key: 'totalProducts', label: 'Products', value: this.totalProducts, icon: 'storefront', route: 'manage-product-list' },
      ];
    },
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Dashboard', active: true },
      ];
    },
  },

  methods: {
    ...mapActions('school', []),
    ...mapActions('program', ['getSchoolProgramAggregate']),
    ...mapActions('student', ['getSchoolStudentAggregate', 'getSchoolRegAggregate']),
    ...mapActions('classes', ['getSchoolClassesAggregate']),
    ...mapActions('instructor', ['getSchoolInstructorsAggregate']),
    ...mapActions('products', ['getAllProducts']),
    ...mapActions('notifications', ['getAllNotifications', 'updateNotification']),

    async fetchStudentsCount() {
      try {
        this.isLoading.totalStudents = true;
        const response = await this.getSchoolStudentAggregate({});
        this.totalStudents = response.data.count;
        this.isLoading.totalStudents = false;
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },
    async fetchProgramsCount() {
      try {
        this.isLoading.totalPrograms = true;
        const response = await this.getSchoolProgramAggregate({});
        this.totalPrograms = response.data.count;
        this.isLoading.totalPrograms = false;
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },

    async fetchClassesCount() {
      try {
        this.isLoading.totalClasses = true;
        const response = await this.getSchoolClassesAggregate({
          ...(get(this.filters.dateRange, '0') && {
            start_date_from: this.filters.dateRange[0],
            start_date_to: this.filters.dateRange[1],
          }),
        });
        this.totalClasses = response.data.count;
        this.isLoading.totalClasses = false;
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },

    async fetchRegistrationCount() {
      try {
        this.isLoading.totalRegistrations = true;
        const response = await this.getSchoolRegAggregate({
          ...(get(this.filters.dateRange, '0') && {
            start_date: this.filters.dateRange[0],
            end_date: this.filters.dateRange[1],
          }),
        });
        this.totalRegistrations = response.data.count;
        this.isLoading.totalRegistrations = false;
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },

    async fetchInstructorsCount() {
      try {
        this.isLoading.totalInstructors = true;
        const response = await this.getSchoolInstructorsAggregate({});
        this.totalInstructors = response.data.count;
        this.isLoading.totalInstructors = false;
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },

    async fetchProductsCount() {
      try {
        this.isLoading.totalProducts = true;
        const response = await this.getAllProducts({ limit: 1, offset: 0 });
        this.totalProducts = response.data.count;
        this.isLoading.totalProducts = false;
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },

    updateFilters() {
      this.fetchRegistrationCount();
      this.fetchClassesCount();
    },

    async fetchNotifications() {
      this.areNotificationsLoading = true;
      const response = await this.getAllNotifications({
        notification_type__type_abbreviation: 'app_feature_update',
      });
      if (response.data) {
        this.allNotifications = response.data;

        const unReadNotification = (this.pendingNotifications = this.allNotifications.filter(
          notification => notification.is_read === false
        ));

        this.pendingNotifications = this.allNotifications
          .filter(notification => notification.is_read === false)
          .slice(0, 3);

        this.moreNotifications = unReadNotification.length - this.pendingNotifications.length;
      }

      this.areNotificationsLoading = false;
    },

    async setNotificationRead(id) {
      const data = { is_read: true };
      await this.updateNotification({ id: id, data });
      this.fetchNotifications();
    },
  },

  async mounted() {
    this.filters.dateRange = [formatToAPIDate(moment().startOf('month')), formatToAPIDate(moment().endOf('month'))];

    this.fetchProgramsCount();
    this.fetchStudentsCount();
    this.fetchClassesCount();
    this.fetchRegistrationCount();
    this.fetchInstructorsCount();
    this.fetchProductsCount();
    this.fetchNotifications();
  },
};
</script>
